/**
 * AttachmentViewer — shared renderer for a mixed grid of photo + video
 * attachments across Incident, Maintenance, DAR, and Portal report modals.
 * Videos get an inline <video controls> player + "Download original" link.
 * Images keep the existing "open in new tab" tile behaviour.
 */
import { resolveMediaUrl } from "../lib/api";
import MediaImage from "./MediaImage";

export function isVideoAttachment(p) {
  if (!p) return false;
  if ((p.kind || "").toLowerCase() === "video") return true;
  if ((p.content_type || "").startsWith("video/")) return true;
  const url = String(p.url || p.storage_path || "").toLowerCase();
  const bare = url.split("?")[0].split("#")[0];
  const ext = bare.includes(".") ? bare.split(".").pop() : "";
  return ["mp4", "mov", "webm", "m4v"].includes(ext);
}

function resolveSrc(p) {
  const url = p.url || p.storage_path || "";
  return resolveMediaUrl(url);
}

/**
 * Renders a responsive grid of photo + video tiles.
 * @param {Array} items      photo_attachments array
 * @param {(iso: string) => string} formatTimestamp  optional formatter
 * @param {string} testIdPrefix  test-id prefix (default "attachment")
 */
export default function AttachmentViewer({ items, formatTimestamp, testIdPrefix = "attachment" }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const fmt = formatTimestamp || ((iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
  });
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {items.map((p, i) => {
        const src = resolveSrc(p);
        const isVideo = isVideoAttachment(p);
        if (isVideo) {
          return (
            <div
              key={p.url || p.storage_path || i}
              className="block border border-[#27272A]"
              data-testid={`${testIdPrefix}-video-${i}`}
            >
              <video
                controls
                playsInline
                preload="metadata"
                className="w-full h-40 object-cover bg-black"
                data-testid={`${testIdPrefix}-video-player-${i}`}
              >
                <source src={src} type={p.content_type || "video/mp4"} />
                Your browser cannot play this video.
              </video>
              <div className="px-2 py-1 border-t border-[#27272A] bg-[#0A0A0A] flex items-center justify-between gap-2">
                <a
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-[#FF453A] font-mono"
                  data-testid={`${testIdPrefix}-video-download-${i}`}
                >
                  Download original
                </a>
                {p.captured_at && (
                  <span className="text-[10px] text-zinc-500 font-mono">{fmt(p.captured_at)}</span>
                )}
              </div>
              {p.caption && (
                <div className="text-xs text-zinc-200 px-2 py-1 border-t border-[#27272A] bg-[#0A0A0A]">
                  {p.caption}
                </div>
              )}
            </div>
          );
        }
        return (
          <a
            key={p.url || p.storage_path || i}
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="block border border-[#27272A]"
            data-testid={`${testIdPrefix}-photo-${i}`}
          >
            <MediaImage
              src={src}
              alt={p.caption || `Photo ${i + 1}`}
              className="w-full h-40 object-cover"
              testId={`${testIdPrefix}-photo-img-${i}`}
            />
            {(p.captured_at || p.caption) && (
              <div className="text-xs font-mono text-zinc-400 px-2 py-1 border-t border-[#27272A] bg-[#0A0A0A]">
                {p.captured_at && <div>{fmt(p.captured_at)}</div>}
                {p.caption && <div className="text-zinc-200">{p.caption}</div>}
              </div>
            )}
          </a>
        );
      })}
    </div>
  );
}
